import React, { useState } from "react";

// Set once a host dismisses the card. Read by InviteSection too, for the "new"
// mark on the Popular tab. Versioned so a rewrite of the card can show it again.
export const EXPLAINER_SEEN_KEY = "gist.popularExplainerSeen.v1";

// Storage can be missing or throw (private mode, blocked site data), and the
// card must still render. Unreadable counts as not seen: showing the card again
// is the harmless failure.
export function readExplainerSeen() {
  try {
    return window.localStorage.getItem(EXPLAINER_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function markExplainerSeen() {
  try {
    window.localStorage.setItem(EXPLAINER_SEEN_KEY, "1");
  } catch {
    // Nothing to do: the card simply opens again next time.
  }
}

const HOW_IT_WORKS = [
  "Every time someone opens a member's profile from an event's guest list, we count it. The list ranks members by how many different people did that, not by total clicks.",
  "Switch \"Rank by\" to Join rate to see who helps people decide to come: of the people who looked at a member's profile before joining an event, the share who then joined.",
  "\"All events\" ranks members across every Gist event. Use it to pick who to invite. \"This event\" shows who drew people to your event once people have started looking.",
  "Reach out sends one text from Gist. You can invite them to your event, add one of your coupons, and add a short note. Your note goes out with your first name.",
  "You can also tell them they may be able to earn by coming. Gist pays popular guests a share of what the night makes, after the event. Gist pays it, and it never comes out of your payout. Gist decides who gets booked and paid, so the text only says they may be eligible.",
];

const RULES = [
  "Each member gets at most one earn text a day, from anyone.",
  "Texts only go to members who allow them.",
  "You can reach out to up to 40 people a day from here.",
  "Please keep this list to yourself. It is here to help you fill your events.",
];

/**
 * What Popular guests is for and how it works, at the top of a host's tab.
 *
 * A host is handed this tab by an operator, often with no more than a text, so
 * the first visit opens the whole card. Once they press "Got it" it folds to a
 * one-line header they can open again. `onSeen` lets the tab row drop its "new"
 * mark in the same moment.
 */
export function PopularExplainer({ onSeen }) {
  const [open, setOpen] = useState(() => !readExplainerSeen());

  const dismiss = () => {
    markExplainerSeen();
    setOpen(false);
    if (onSeen) onSeen();
  };

  if (!open) {
    return (
      <div className="mb-4 flex items-center justify-between gap-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Popular guests</span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded="false"
          className="text-sm underline text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
        >
          How this works
        </button>
      </div>
    );
  }

  return (
    <section
      aria-label="How Popular guests works"
      className="mb-4 p-4 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300"
    >
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Popular guests</h3>
      <p className="mt-1">
        These are the members people look at most when they check who is going to an event on Gist.
      </p>

      <h4 className="mt-4 font-medium text-gray-900 dark:text-gray-100">Why use it</h4>
      <p className="mt-1">
        People decide whether to come partly by who else is going. A few members get their profile
        opened far more than anyone else. When they are on your guest list, more people tend to say yes.
      </p>

      <h4 className="mt-4 font-medium text-gray-900 dark:text-gray-100">How it works</h4>
      <ol className="mt-1 list-decimal pl-5 space-y-1">
        {HOW_IT_WORKS.map((line) => <li key={line}>{line}</li>)}
      </ol>

      <h4 className="mt-4 font-medium text-gray-900 dark:text-gray-100">A few rules</h4>
      <ul className="mt-1 list-disc pl-5 space-y-1">
        {RULES.map((line) => <li key={line}>{line}</li>)}
      </ul>

      <button
        type="button"
        onClick={dismiss}
        className="mt-4 w-full sm:w-auto px-4 py-2 rounded-md font-medium bg-primary text-white hover:bg-primaryDark transition-colors"
      >
        Got it
      </button>
    </section>
  );
}

export default PopularExplainer;
